import { LOCALES, type Locale } from '@sjuni/types';
import { cn, Input, Textarea } from '@sjuni/ui';
import { AlertTriangle, Copy } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import {
  copyLocale,
  hasContent,
  REQUIRED_LOCALE,
  setLocale,
  type TranslatedValue,
  valueFor,
} from '@/lib/forms/translatable';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.1 — every translatable field, wrapped:
 *
 *   ┌ სათაური ─────────────────────────── [KA] [EN] ─┐
 *   │ ცხადდება მიღება 60 კრედიტიან პროგრამაზე        │
 *   └────────────────────────────────────────────────┘
 *      ⚠ English translation missing
 *
 * Three rules are doing the work.
 *
 * "Tab switches locale without losing state; both locales submit together." So
 * the value is always the whole per-locale map and only the *view* changes —
 * there is no per-locale form state to lose, and nothing to reconcile on save.
 *
 * "Georgian is required; English is optional but its absence is always visible,
 * never silent." The warning is not an error: it does not block saving, it
 * blocks *not noticing*. CLAUDE.md rule 5 is the whole reason — the current
 * English site is a stale partial mirror that says nothing about being one.
 *
 * The locale toggle here is bound to the shell's KA|EN control, so every field
 * on a screen moves together. A per-field tab would let an editor leave one
 * field on English and the next on Georgian and never see it.
 */
export interface TranslatableFieldProps {
  label: string;
  value: TranslatedValue | null | undefined;
  onChange: (next: TranslatedValue) => void;
  /** §6.9 — "every non-obvious field gets an inline hint". */
  hint?: string;
  error?: string;
  /** Georgian is required unless the field is genuinely optional. */
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  /** Name attribute, so `applyApiErrors` can scroll to the offending field. */
  name?: string;
  disabled?: boolean;
}

export function TranslatableField({
  label,
  value,
  onChange,
  hint,
  error,
  required = true,
  multiline = false,
  rows = 4,
  name,
  disabled,
}: TranslatableFieldProps) {
  const { t } = useTranslation();
  const { locale, setLocale: setEditorLocale } = useEditorLocale();
  const groupId = useId();

  const Control = multiline ? Textarea : Input;

  return (
    <div>
      <div className="mb-1 flex items-end justify-between gap-3">
        {/* The label belongs to the control, which `Input` renders — so this
            row is the locale toggle and nothing else. The visible label comes
            from the control below. */}
        <div
          role="radiogroup"
          aria-label={t('form.localeFor', { field: label })}
          id={groupId}
          className="ms-auto flex items-center gap-1"
        >
          {LOCALES.map((candidate) => {
            const filled = hasContent(value, candidate);

            return (
              <button
                key={candidate}
                type="button"
                role="radio"
                aria-checked={locale === candidate}
                onClick={() => setEditorLocale(candidate)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-caption font-extrabold uppercase transition-colors duration-fast',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600',
                  locale === candidate
                    ? 'bg-navy-600 text-white'
                    : 'bg-surface-alt text-secondary hover:text-primary',
                )}
              >
                {candidate}
                {/*
                  A dot rather than a tick or a cross: the toggle has to say
                  which locales have content *without* reading as an error,
                  because an untranslated English field is a normal state.
                */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'ms-1 inline-block h-1.5 w-1.5 rounded-full align-middle',
                    filled ? 'bg-success' : 'bg-current opacity-40',
                  )}
                />
                <span className="sr-only">
                  {filled ? t('form.localeFilled') : t('form.localeEmpty')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Control
        label={label}
        // Only Georgian is ever required — §6 and rule 5. The asterisk follows
        // the toggle, so switching to English drops it.
        required={required && locale === REQUIRED_LOCALE}
        hint={hint}
        error={error}
        name={name}
        disabled={disabled}
        {...(multiline ? { rows } : {})}
        value={valueFor(value, locale)}
        onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          onChange(setLocale(value, locale, event.target.value))
        }
      />

      <Missing
        value={value}
        locale={locale}
        onCopy={() => onChange(copyLocale(value, REQUIRED_LOCALE, locale))}
      />
    </div>
  );
}

/**
 * The "⚠ English translation missing" line, and §4.1's Copy KA → EN beside it.
 *
 * Shown against the locale currently on screen, so it appears when the editor
 * is looking at the empty field rather than as a summary they have to decode.
 */
function Missing({
  value,
  locale,
  onCopy,
}: {
  value: TranslatedValue | null | undefined;
  locale: Locale;
  onCopy: () => void;
}) {
  const { t } = useTranslation();

  // Nothing to say when the field has content, and nothing useful to say about
  // the required locale — that one is an error, which the control shows.
  if (locale === REQUIRED_LOCALE || hasContent(value, locale)) {
    return null;
  }

  const canCopy = hasContent(value, REQUIRED_LOCALE);

  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-2 text-caption text-muted">
      <AlertTriangle size={14} strokeWidth={1.75} aria-hidden="true" />
      {t('form.translationMissing', { locale: locale.toUpperCase() })}

      {canCopy ? (
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 font-semibold text-link underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        >
          <Copy size={12} strokeWidth={1.75} aria-hidden="true" />
          {t('form.copyFromGeorgian')}
        </button>
      ) : null}
    </p>
  );
}
